import { plugin as engagementPlugin } from '@amplitude/engagement-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import {
  AmplitudeBrowserTrackingClient,
  createEventsCore,
} from '@apollo-frontend/tracking-core';

import { redactSensitiveUrlsPlugin } from 'src/lib/analytics/amplitude/redactSensitiveUrlsPlugin';
import Config from 'src/lib/config';
import { isCypressEnvironment } from 'src/lib/isCypressUser';

import { AmplitudeApiKey } from './amplitudeApiKey';
import type { TrackingEvents } from './generated/taxonomy';

const { amplitudeOptions } = Config.settings;

let amplitudeClient: AmplitudeBrowserTrackingClient | undefined;

/**
 * Creates the single Amplitude instance for the main Studio app. Product
 * events, identify, Session Replay, and Guides & Surveys all share its device
 * and session IDs.
 *
 * Creating it sends requests and sets cookies, so only call this once the
 * visitor is logged in or has given Performance consent. Safe to call more
 * than once.
 */
export const startAmplitude = () => {
  amplitudeClient ??= AmplitudeBrowserTrackingClient.create({
    ...amplitudeOptions,
    apiKey: AmplitudeApiKey[amplitudeOptions.environment],
    product: 'studio',
    client: {
      configuration: {
        ...amplitudeOptions.client?.configuration,
        appVersion: Config.gitCommitHash,
      },
    },
    // Plugins must be added before init; Cypress runs never record Session Replay.
    // Redaction first, so it sees everything the others add. See OE-1393.
    plugins: [
      redactSensitiveUrlsPlugin(),
      ...(isCypressEnvironment() ? [] : [sessionReplayPlugin()]),
      engagementPlugin(),
    ],
  });
};

/** The Amplitude client, or `undefined` until `startAmplitude` runs. */
export const getAmplitudeClient = () => amplitudeClient;

// createEventsCore reads its client once, at import, so forward to whichever
// client exists at call time. Events before Amplitude starts are dropped.
export const events = createEventsCore<TrackingEvents>(() => ({
  track: async (eventName, data) => amplitudeClient?.track(eventName, data),
  identify: async (data) => amplitudeClient?.identify(data),
}));
